import { Globe, Loader2, Wrench } from 'lucide-react';

import { KeyValueTable } from '@/components/key-value-table';

import { useToolDetailQuery } from '@/query/tool.query';

import { useToolsStore } from '@/store/tools.store';
import { type MCPServerHTTPConfig, TOOL_TYPE } from '@/types/tool.type';

function SectionTitle({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold">
      {icon}
      {children}
    </h3>
  );
}

export function ToolDetail() {
  const selectedTool = useToolsStore(state => state.selectedTool);

  const { data: tool, isLoading } = useToolDetailQuery(selectedTool?.id ?? '', {
    enabled: !!selectedTool?.id,
  });

  if (!selectedTool) return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!tool) return null;

  const isMCP = tool.type === TOOL_TYPE.MCP;
  const mcpConfig = isMCP ? (tool.config as MCPServerHTTPConfig | null) : null;
  const headerEntries = mcpConfig?.headers
    ? (Object.entries(mcpConfig.headers) as [string, string][])
    : [];
  const queryParamEntries = mcpConfig?.query_params
    ? (Object.entries(mcpConfig.query_params) as [string, string][])
    : [];

  return (
    <div className="space-y-5 p-4 sm:space-y-6 sm:p-5">
      {isMCP && (
        <section className="space-y-3">
          <SectionTitle icon={<Globe className="h-4 w-4 text-muted-foreground" />}>
            Endpoint
          </SectionTitle>

          <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <span className="inline-flex items-center rounded border border-blue-200 bg-blue-50 px-2 py-0.5 font-mono text-xs font-semibold text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
                {(mcpConfig?.transport ?? 'http').toUpperCase()}
              </span>
              <code className="flex-1 break-all text-sm text-foreground">
                {mcpConfig?.url || '—'}
              </code>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border bg-background px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Request Timeout
                </p>
                <p className="mt-1 text-sm font-medium">
                  {mcpConfig?.timeout !== undefined ? `${mcpConfig.timeout} ms` : '—'}
                </p>
              </div>

              <div className="rounded-md border bg-background px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Session Timeout
                </p>
                <p className="mt-1 text-sm font-medium">
                  {mcpConfig?.client_session_timeout_seconds !== undefined
                    ? `${mcpConfig.client_session_timeout_seconds} ms`
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {headerEntries.length > 0 && (
        <section className="space-y-3">
          <SectionTitle>Headers</SectionTitle>
          <KeyValueTable entries={headerEntries} />
        </section>
      )}

      {queryParamEntries.length > 0 && (
        <section className="space-y-3">
          <SectionTitle>Query Parameters</SectionTitle>
          <KeyValueTable entries={queryParamEntries} />
        </section>
      )}

      {!isMCP && (
        <section className="space-y-3">
          <SectionTitle icon={<Wrench className="h-4 w-4 text-muted-foreground" />}>
            Configuration
          </SectionTitle>
          <div className="rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground">
            No additional configuration is available for this tool type.
          </div>
        </section>
      )}
    </div>
  );
}
